import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Loader2, Check, Circle, Copy, Shield, Clock, CalendarClock, X } from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import {
  getAthleteRouteForMilestone,
  isMilestoneActionable,
  getMilestoneByName,
} from '@adthlete/common/utils/milestoneConfig'
import { CreateCollaborationModal } from '../CreateCollaborationModal'
import { formatDateOnly, getDateOnlyDiffInDays } from '@/lib/dateOnly'
import {
  useSubmitMilestoneCheckinMutation,
  useCancelMilestoneDateProposalMutation,
} from '@/redux/collaborationsApi'
import RequestDeadlineExtensionDialog from './RequestDeadlineExtensionDialog'

const formatCheckInRelative = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(ms / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

interface CollaborationStatusCardProps {
  campaign: any
  detailLoading: boolean
  containerInvokeClass?: string
  hasVideoSuggestion?: boolean
  hasConceptSuggestion?: boolean
}

const CollaborationStatusCard = ({
  campaign: collaboration,
  detailLoading,
  containerInvokeClass,
  hasVideoSuggestion = false,
  hasConceptSuggestion = false,
}: CollaborationStatusCardProps) => {
  const [submitCheckin, { isLoading: submittingCheckin }] = useSubmitMilestoneCheckinMutation()
  const [cancelProposal, { isLoading: cancellingProposal }] =
    useCancelMilestoneDateProposalMutation()
  const [extensionMilestone, setExtensionMilestone] = useState<{
    id: string
    name: string
    dueDate: string | null
  } | null>(null)

  const handleCheckInOnTrack = async (milestoneId: string) => {
    if (!collaboration?.id) return
    try {
      await submitCheckin({
        campaignId: collaboration.id,
        milestoneId,
        response: 'on_track',
      }).unwrap()
      toast.success('Confirmed on track')
    } catch (err) {
      console.error('Failed to record check-in:', err)
      toast.error('Could not record check-in. Please try again.')
    }
  }

  const handleCancelProposal = async (milestoneId: string, proposalId: string) => {
    if (!collaboration?.id) return
    try {
      await cancelProposal({
        campaignId: collaboration.id,
        milestoneId,
        proposalId,
      }).unwrap()
      toast.success('Request withdrawn')
    } catch (err) {
      console.error('Failed to cancel proposal:', err)
      toast.error('Could not withdraw request. Please try again.')
    }
  }

  const getProgress = () => {
    if (collaboration.status === 'live') return 100
    if (!collaboration.milestones || collaboration.milestones.length === 0) return 0
    const totalMilestones = collaboration.milestones.length
    const completedMilestones = collaboration.milestones.filter(
      (m: any) => m.status === 'approved' || m.status === 'completed',
    ).length
    return Math.round((completedMilestones / totalMilestones) * 100)
  }

  const progressValue = getProgress()

  // Helper functions moved inside or could be imported utils
  const formatDate = (dateString: string) => {
    return (
      formatDateOnly(dateString, 'en-US', {
        month: 'short',
        day: 'numeric',
      }) ?? ''
    )
  }

  const getStatusBadge = (dueDate: string | null | undefined, status: string, milestone: any) => {
    if (status === 'revision_requested') {
      return (
        <Badge className="bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 text-[10px] font-medium">
          Revisions Requested
        </Badge>
      )
    }

    const hasPendingReview = milestone.submissions?.some(
      (sub: any) => sub.status === 'pending_review',
    )

    if (hasPendingReview) {
      return (
        <Badge className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 text-[10px] font-medium">
          Pending Review
        </Badge>
      )
    }

    if (status === 'approved' || status === 'completed') return null

    const daysUntilDue = getDateOnlyDiffInDays(dueDate)

    if (daysUntilDue === null) return null

    if (daysUntilDue < -2) {
      return (
        <Badge className="bg-red-500/20 text-red-400 hover:bg-red-500/30 text-[10px] font-medium">
          Overdue
        </Badge>
      )
    } else if (daysUntilDue <= 2 && daysUntilDue >= 0) {
      return (
        <Badge className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 text-[10px] font-medium">
          Due Soon
        </Badge>
      )
    }
    return null
  }

  const getMilestoneLink = (milestoneName: string) => {
    if (!collaboration?.id) return null
    return getAthleteRouteForMilestone(milestoneName, collaboration.id)
  }

  const isClickable = (milestone: any) => {
    return (
      isMilestoneActionable(milestone.name) &&
      (milestone.status === 'pending' ||
        milestone.status === 'submitted' ||
        milestone.status === 'approved' ||
        milestone.status === 'completed' ||
        milestone.status === 'revision_requested')
    )
  }

  return (
    <>
      {extensionMilestone && (
        <RequestDeadlineExtensionDialog
          open={!!extensionMilestone}
          onOpenChange={(open) => {
            if (!open) setExtensionMilestone(null)
          }}
          campaignId={collaboration.id}
          milestoneId={extensionMilestone.id}
          milestoneName={extensionMilestone.name}
          currentDueDate={extensionMilestone.dueDate}
        />
      )}
      <Card variant="dark" className={`${containerInvokeClass}`}>
        <CardHeader>
          <CardTitle className="text-white">Collaboration Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-4xl font-light text-white">{progressValue}%</span>
              <span className="text-gray-400">complete</span>
            </div>
            <Progress
              value={progressValue}
              className="h-2 bg-gray-700"
              indicatorClassName="bg-[#C0FD72]"
            />
          </div>

          {collaboration.status === 'pending_brand' && collaboration.invitationLink && (
            <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
              <h3 className="text-sm font-medium text-white mb-2">Invitation Link</h3>
              <p className="text-xs text-gray-400 mb-3">
                Share this link with the brand to invite them to this collaboration.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-black/30 px-3 py-2 rounded text-xs text-gray-300 truncate font-mono">
                  {collaboration.invitationLink}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 border-white/10 hover:bg-white/5 text-xs bg-transparent text-white"
                  onClick={() => {
                    navigator.clipboard.writeText(collaboration.invitationLink)
                    toast.success('Link copied to clipboard')
                  }}
                >
                  <Copy className="h-3.5 w-3.5 mr-1" />
                  Copy
                </Button>
              </div>
            </div>
          )}

          {collaboration.status === 'pending_brand' && !collaboration.invitationLink && (
            <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
              <h3 className="text-sm font-medium text-white mb-2">Invitation Pending</h3>
              <p className="text-xs text-gray-400 mb-3">
                This collaboration is waiting for a brand, but no invitation has been sent yet.
              </p>
              <CreateCollaborationModal
                initialStep="contact"
                initialCollaborationId={collaboration.id}
                trigger={
                  <Button size="sm" variant="lime" className="w-full">
                    Generate Invitation Link
                  </Button>
                }
              />
            </div>
          )}

          <div className="space-y-1">
            {detailLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-[#C0FD72]" />
              </div>
            ) : collaboration?.milestones && collaboration.milestones.length > 0 ? (
              collaboration.milestones.flatMap((milestone: any, milestoneIndex: number) => {
                const link = getMilestoneLink(milestone.name)
                const clickable = isClickable(milestone)
                const isDone = milestone.status === 'approved' || milestone.status === 'completed'
                const statusBadge = getStatusBadge(milestone.dueDate, milestone.status, milestone)
                const isAgreeOnTerms = milestone.name?.toLowerCase().includes('agree')

                const milestoneDef = getMilestoneByName(milestone.name)
                const isVideoMilestone = milestoneDef?.sequenceOrder === 2
                const isConceptMilestone = milestoneDef?.sequenceOrder === 1
                const hasActiveSubmission = milestone.submissions?.some((s: any) =>
                  ['pending_review', 'approved'].includes(s.status),
                )
                const showVideoSuggestionPill =
                  hasVideoSuggestion && isVideoMilestone && !hasActiveSubmission && !isDone
                const showConceptSuggestionPill =
                  hasConceptSuggestion && isConceptMilestone && !hasActiveSubmission && !isDone

                const nilGoConfirmed = collaboration.nilGoStatus === 'confirmed'
                const rawPayout =
                  collaboration.payout != null ? parseFloat(collaboration.payout) : null
                const complianceNeeded = rawPayout === null || rawPayout >= 600

                const hasAgreeOnTerms = collaboration.milestones.some((m: any) =>
                  m.name?.toLowerCase().includes('agree'),
                )
                // Inject compliance after agree_on_terms when present, or before the
                // first content milestone when there is no agree_on_terms (manager-created collabs)
                const injectComplianceHere =
                  isAgreeOnTerms || (!hasAgreeOnTerms && milestoneIndex === 0)

                const rows = [
                  <div
                    key={milestone.id}
                    className="flex items-start gap-3 py-3 border-b border-white/5 last:border-0"
                  >
                    <div className="mt-0.5 shrink-0">
                      {isDone ? (
                        <Check className="w-4 h-4 text-[#C0FD72]" />
                      ) : milestone.status === 'submitted' ? (
                        <Circle className="w-4 h-4 text-blue-400" />
                      ) : (
                        <Circle className="w-4 h-4 text-gray-600" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      {clickable && link ? (
                        <Link
                          to={link}
                          className={`text-sm font-medium underline-offset-2 underline ${
                            isDone ? 'text-gray-400' : 'text-white'
                          } hover:text-[#C0FD72]`}
                        >
                          {milestone.name}
                        </Link>
                      ) : (
                        <span
                          className={`text-sm ${
                            isDone
                              ? 'text-gray-400'
                              : milestone.status === 'pending'
                                ? 'text-gray-500'
                                : 'text-white font-medium'
                          }`}
                        >
                          {milestone.name}
                        </span>
                      )}

                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[11px] text-gray-500">
                          {isDone
                            ? milestone.completedAt
                              ? formatDate(milestone.completedAt)
                              : formatDate(milestone.dueDate)
                            : milestone.dueDate
                              ? `Due ${formatDate(milestone.dueDate)}`
                              : ''}
                        </span>
                        {statusBadge}
                        {showConceptSuggestionPill && (
                          <Badge className="bg-[#C0FD72]/20 text-[#C0FD72] hover:bg-[#C0FD72]/30 text-[10px] font-medium border border-[#C0FD72]/30">
                            Suggestion Available
                          </Badge>
                        )}
                        {showVideoSuggestionPill && (
                          <Badge className="bg-[#C0FD72]/20 text-[#C0FD72] hover:bg-[#C0FD72]/30 text-[10px] font-medium border border-[#C0FD72]/30">
                            Video to Review
                          </Badge>
                        )}
                        {milestone.checkIn?.response === 'on_track' &&
                          milestone.checkIn.responseAt && (
                            <Badge className="bg-[#C0FD72]/20 text-[#C0FD72] hover:bg-[#C0FD72]/30 text-[10px] font-medium border border-[#C0FD72]/30">
                              <Check className="h-3 w-3 mr-1" />
                              On track · {formatCheckInRelative(milestone.checkIn.responseAt)}
                            </Badge>
                          )}
                      </div>

                      {milestone.checkIn?.active && (
                        <div className="mt-2 p-3 bg-white/5 border border-white/10 rounded-lg">
                          <div className="flex items-start gap-2 mb-2">
                            <Clock className="h-3.5 w-3.5 text-[#C0FD72] mt-0.5 shrink-0" />
                            <p className="text-xs text-gray-300 leading-relaxed">
                              Brand is checking in — confirm you're on track for the{' '}
                              {milestone.dueDate ? formatDate(milestone.dueDate) : 'upcoming'}{' '}
                              deadline.
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="lime"
                              disabled={submittingCheckin}
                              onClick={() => handleCheckInOnTrack(milestone.id)}
                              className="h-7 text-[11px] px-3"
                            >
                              <Check className="h-3 w-3 mr-1" />
                              On track
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                setExtensionMilestone({
                                  id: milestone.id,
                                  name: milestone.name,
                                  dueDate: milestone.dueDate ?? null,
                                })
                              }
                              className="h-7 text-[11px] px-3 border-white/15 text-gray-300 bg-transparent hover:bg-white/5"
                            >
                              <Clock className="h-3 w-3 mr-1" />
                              Need more time
                            </Button>
                          </div>
                        </div>
                      )}

                      {milestone.dateProposal?.status === 'open' && (
                        <div className="mt-2 p-3 bg-[#F9DA4C]/10 border border-[#F9DA4C]/25 rounded-lg">
                          <div className="flex items-start gap-2">
                            <CalendarClock className="h-3.5 w-3.5 text-[#F9DA4C] mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#F9DA4C]">
                                New deadline proposed · waiting on brand
                              </p>
                              <p className="text-xs text-white mt-0.5">
                                {formatDate(milestone.dateProposal.proposedDueDate)}
                                {milestone.dateProposal.reason && (
                                  <span className="text-gray-400">
                                    {' · '}
                                    {milestone.dateProposal.reason}
                                  </span>
                                )}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                handleCancelProposal(milestone.id, milestone.dateProposal!.id)
                              }
                              disabled={cancellingProposal}
                              title="Withdraw request"
                              className="text-gray-500 hover:text-white transition-colors shrink-0"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>,
                ]

                {
                  /* Inject Compliance row after Agree on Terms */
                }
                if (injectComplianceHere) {
                  rows.push(
                    <Link
                      key="compliance-step"
                      to={`/collaborations/${collaboration.id}?tab=compliance`}
                      preventScrollReset
                      className="flex items-start gap-3 py-3 border-b border-white/5 group"
                    >
                      <div className="mt-0.5 shrink-0">
                        {nilGoConfirmed ? (
                          <Check className="w-4 h-4 text-[#C0FD72]" />
                        ) : (
                          <Shield className="w-4 h-4 text-[#F9DA4C]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span
                          className={`text-sm font-medium group-hover:text-[#C0FD72] ${nilGoConfirmed ? 'text-gray-400' : 'text-white'}`}
                        >
                          Compliance
                        </span>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[11px] text-gray-500">
                            {nilGoConfirmed
                              ? 'Submitted'
                              : complianceNeeded
                                ? 'NIL Go submission required'
                                : 'Below $600 threshold'}
                          </span>
                          {!nilGoConfirmed && complianceNeeded && (
                            <Badge className="bg-[#F9DA4C]/20 text-[#F9DA4C] hover:bg-[#F9DA4C]/30 text-[10px] font-medium">
                              Action Needed
                            </Badge>
                          )}
                        </div>
                      </div>
                    </Link>,
                  )
                }

                return rows
              })
            ) : (
              <p className="text-gray-400 text-sm">No milestones available</p>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  )
}

export default CollaborationStatusCard
